import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Popover from "@mui/material/Popover";
import Popper from "@mui/material/Popper";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Button from "@mui/material/Button";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SkuflixListVideoIcon from "core/icons/skuflix/SkuflixListVideoIcon";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import { StreamEpgItemsQuery } from "core/queries";
import PlayerFooterButton from "core/components/PlayerFooterButton";
import moment from "core/utils/moment";

function formatTime(date) {
  return moment(date).format("HH:mm");
}

function getProgress(dateFrom, dateTo) {
  const now = Date.now();
  const start = new Date(dateFrom).getTime();
  const end = new Date(dateTo).getTime();

  if (now < start) return 0;
  if (now > end) return 100;

  return ((now - start) / (end - start)) * 100;
}

function isCurrent(dateFrom, dateTo) {
  const now = Date.now();
  return (
    now >= new Date(dateFrom).getTime() && now < new Date(dateTo).getTime()
  );
}

function EpgButton({ contentId, variant }) {
  const { t } = useTranslation("core");
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width: 450px)");
  const [open, setOpen] = useState(false);
  const [infoAnchorEl, setInfoAnchorEl] = useState(null);
  const [infoText, setInfoText] = useState("");
  const [scheduleAnchorEl, setScheduleAnchorEl] = useState(null);
  const [, setTick] = useState(0);

  const { data } = useQuery(StreamEpgItemsQuery, {
    variables: { contentId, limit: 4 },
    pollInterval: 60000,
  });

  const items = data?.streamEpgItems || [];

  useEffect(() => {
    if (!open) return;

    const interval = setInterval(() => {
      setTick((v) => v + 1);
    }, 10000);

    return () => clearInterval(interval);
  }, [open]);

  // Перекрываем MuiDrawer / MuiPaper из theme (там paperColor), нужен фон арены (body)
  const epgPanelSurfaceSx = {
    bgcolor: theme.palette.background.body,
    backgroundImage: "none",
    "&&": {
      backgroundColor: theme.palette.background.body,
    },
  };

  const epgInfoPopoverPaperSx = {
    px: 1.5,
    py: 1,
    maxWidth: 260,
    bgcolor: theme.palette.tooltip.main,
    backgroundImage: "none",
    "&&": {
      backgroundColor: theme.palette.tooltip.main,
    },
  };

  // Modal + Portal в body; шапка arena z-index 1200 — поднимаем корень выше, чтобы не перекрывали
  const epgInfoPopoverSlotProps = {
    root: {
      sx: {
        zIndex: theme.zIndex.tooltip,
      },
    },
    paper: {
      sx: epgInfoPopoverPaperSx,
    },
  };

  const epgSchedulePopperPlacement =
    variant === "outlined" ? "bottom-start" : "top-start";
  const epgSchedulePopperModifiers = [
    {
      name: "flip",
      enabled: true,
      options: {
        fallbackPlacements: ["top-end", "bottom-start", "bottom-end"],
      },
    },
    {
      name: "preventOverflow",
      enabled: true,
      options: {
        padding: 8,
        boundary: "viewport",
        altBoundary: true,
        tether: true,
      },
    },
  ];

  if (items.length === 0) return null;

  const handleClick = (event) => {
    setScheduleAnchorEl(event.currentTarget);
    setOpen((v) => !v);
  };

  const handleClose = () => {
    setScheduleAnchorEl(null);
    setOpen(false);
  };

  const handleOpenInfo = (event, text) => {
    event.stopPropagation();
    setInfoAnchorEl(event.currentTarget);
    setInfoText(text || "");
  };

  const handleCloseInfo = () => {
    setInfoAnchorEl(null);
    setInfoText("");
  };

  const button =
    variant === "outlined" ? (
      <Button
        variant="outlined"
        color="inherit"
        startIcon={<SkuflixListVideoIcon />}
        onClick={handleClick}
        sx={{ whiteSpace: "nowrap" }}
      >
        {t("epgButton.title")}
      </Button>
    ) : (
      <PlayerFooterButton
        title={open ? "" : t("epgButton.title")}
        onClick={handleClick}
      >
        <SkuflixListVideoIcon />
      </PlayerFooterButton>
    );

  const listItems = items.map((item, index) => {
    const current = isCurrent(item.dateFrom, item.dateTo);
    const progress = current ? getProgress(item.dateFrom, item.dateTo) : 0;

    return (
      <ListItem
        key={item.id}
        divider={index < items.length - 1}
        secondaryAction={
          item.description ? (
            isMobile ? (
              <IconButton
                edge="end"
                size="small"
                onClick={(event) => handleOpenInfo(event, item.description)}
              >
                <InfoOutlinedIcon fontSize="inherit" />
              </IconButton>
            ) : (
              <Tooltip title={item.description} arrow>
                <IconButton edge="end" size="small">
                  <InfoOutlinedIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )
          ) : null
        }
        sx={{
          position: "relative",
          overflow: "hidden",
          backgroundColor: current
            ? theme.palette.mode === "dark"
              ? theme.palette.tooltip.main
              : "action.selected"
            : "transparent",
        }}
      >
        <ListItemText
          primary={item.title}
          secondary={formatTime(item.dateFrom)}
          primaryTypographyProps={{
            sx: {
              fontWeight: current ? 600 : 400,
            },
          }}
        />
        {current && (
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: 3,
              borderRadius: 0,
            }}
          />
        )}
      </ListItem>
    );
  });

  if (isMobile) {
    return (
      <>
        <Box sx={{ position: "relative" }}>{button}</Box>
        <Drawer
          anchor="bottom"
          open={open}
          onClose={handleClose}
          disableScrollLock
          PaperProps={{
            sx: {
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              maxHeight: "60vh",
              ...epgPanelSurfaceSx,
            },
          }}
        >
          <Typography sx={{ py: 1, px: 2 }}>{t("epgButton.title")}</Typography>
          <List disablePadding dense>
            {listItems}
          </List>
        </Drawer>
        <Popover
          open={Boolean(infoAnchorEl)}
          anchorEl={infoAnchorEl}
          onClose={handleCloseInfo}
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          slotProps={epgInfoPopoverSlotProps}
        >
          <Typography variant="body2">{infoText}</Typography>
        </Popover>
      </>
    );
  }

  return (
    <>
      <ClickAwayListener onClickAway={handleClose}>
        <Box sx={{ position: "relative" }}>
          {button}
          <Popper
            open={open}
            anchorEl={scheduleAnchorEl}
            placement={epgSchedulePopperPlacement}
            disablePortal
            transition
            modifiers={epgSchedulePopperModifiers}
            sx={{
              zIndex: theme.zIndex.tooltip,
            }}
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                timeout={140}
                style={{
                  transformOrigin: String(placement).startsWith("top")
                    ? "left bottom"
                    : "left top",
                }}
              >
                <Paper
                  sx={{
                    overflow: "hidden",
                    minWidth: 250,
                    maxWidth: "min(360px, calc(100vw - 16px))",
                    boxSizing: "border-box",
                    ...epgPanelSurfaceSx,
                  }}
                >
                  <Typography sx={{ py: 1, px: 2 }}>
                    {t("epgButton.title")}
                  </Typography>

                  <List disablePadding dense>
                    {listItems}
                  </List>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Box>
      </ClickAwayListener>
      <Popover
        open={Boolean(infoAnchorEl)}
        anchorEl={infoAnchorEl}
        onClose={handleCloseInfo}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        slotProps={epgInfoPopoverSlotProps}
      >
        <Typography variant="body2">{infoText}</Typography>
      </Popover>
    </>
  );
}

EpgButton.propTypes = {
  contentId: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(["outlined"]),
};

export default EpgButton;
